import type { BrowserCallingCapability, VoicePlatformStatus } from "./api";
import type { EmployeeVoiceChangedPayload } from "../contact-center/realtime/contact-center-realtime-types";

const labels: Record<BrowserCallingCapability["state"], string> = {
  AVAILABLE: "Available",
  PROVISIONING: "Provisioning",
  UNAVAILABLE: "Unavailable",
  UNKNOWN: "Status unavailable",
};

const reasons: Record<string, string> = {
  voice_disabled: "Voice calling is disabled for your employee account.",
  extension_missing: "No voice extension is assigned to your employee account.",
  extension_disabled: "Your employee voice extension is disabled.",
  browser_webrtc_not_enabled:
    "Browser calling has not been provisioned for your employee account.",
  browser_endpoint_not_provisioned:
    "Browser calling has not been provisioned for your employee account.",
  endpoint_not_provisioned:
    "Browser calling has not been provisioned for your employee account.",
  browser_session_not_allowed:
    "Your employee account cannot start a browser calling session.",
  browser_capability_denied:
    "Your employee account cannot start a browser calling session.",
  calling_not_allowed:
    "Your employee account is not allowed to place internal or external calls.",
  calling_capability_denied:
    "Your employee account is not allowed to place internal or external calls.",
  readiness_stale: "The last employee readiness status is out of date.",
  readiness_unavailable: "Current employee readiness could not be confirmed.",
  readiness_missing: "Employee voice readiness has not been reported yet.",
  endpoint_unregistered:
    "Your browser phone is not registered yet. Refresh after registration completes.",
  endpoint_unavailable:
    "Your browser phone is currently unavailable. Refresh its Telephony status and try again.",
  endpoint_stale:
    "Your browser phone status is stale. Refresh its Telephony status before calling.",
  employee_provisioning: "Employee voice setup is still being provisioned.",
  lifecycle_not_ready: "Employee voice setup is still being provisioned.",
  employee_voice_not_active: "Employee voice service is not active.",
  pending_pbx_apply: "Phone provisioning is waiting for the PBX apply to finish.",
  pbx_apply_failed: "The latest phone apply failed. Review Telephony before calling.",
  pbx_config_drifted: "The phone configuration is out of sync. Review Telephony before calling.",
  pbx_unreachable: "The phone system could not be reached. Refresh Telephony status and try again.",
  projection_malformed: "Current employee browser-calling status could not be confirmed.",
  projection_mismatch: "Current employee browser-calling status could not be confirmed.",
};

// The backend returns a sorted, de-duplicated set. Prefer the authoritative
// freshness/PBX reason over generic consequences such as endpoint_not_provisioned.
const actionableReasonPriority = [
  "readiness_stale",
  "readiness_unavailable",
  "endpoint_stale",
  "pbx_apply_failed",
  "pbx_config_drifted",
  "pbx_unreachable",
  "pending_pbx_apply",
];


export type BrowserCallingTerminalPresentation = {
  kind:
    | "not_assigned"
    | "setup_in_progress"
    | "configuration_attention"
    | "no_calling_line";
  label: string;
  summary: string;
};

/**
 * Classifies only capabilities that are authoritatively blocked. Endpoint
 * registration reasons can also describe a valid on-demand preparation path,
 * so they must never become terminal while startMode allows preparation.
 */
export function browserCallingTerminalPresentation(
  capability: Pick<
    BrowserCallingCapability,
    "startMode" | "state" | "reasonCodes"
  >,
): BrowserCallingTerminalPresentation | null {
  if (capability.startMode !== "BLOCKED") return null;
  const reasonCodes = new Set(capability.reasonCodes);
  if (reasonCodes.has("extension_missing")) {
    return {
      kind: "not_assigned",
      label: "No phone assigned",
      summary: "A phone extension has not been assigned to your account.",
    };
  }
  if (
    reasonCodes.has("pending_pbx_apply") ||
    capability.state === "PROVISIONING" ||
    reasonCodes.has("employee_provisioning") ||
    reasonCodes.has("lifecycle_not_ready")
  ) {
    return {
      kind: "setup_in_progress",
      label: "Phone setup in progress",
      summary:
        "Your phone extension is assigned, but its setup has not finished yet.",
    };
  }
  if (
    [
      "pbx_config_drifted",
      "pbx_apply_failed",
      "pbx_unreachable",
      "endpoint_not_provisioned",
      "endpoint_unavailable",
      "endpoint_stale",
    ].some((reason) => reasonCodes.has(reason))
  ) {
    return {
      kind: "configuration_attention",
      label: "Phone configuration needs attention",
      summary:
        "Your assigned phone is not available because its phone-system configuration needs attention.",
    };
  }
  if (reasonCodes.has("no_allowed_line")) {
    return {
      kind: "no_calling_line",
      label: "No calling line available",
      summary:
        "No calling line is currently assigned or permitted for your account.",
    };
  }
  return null;
}

export function isBrowserCallingAvailable(
  status: Pick<
    VoicePlatformStatus,
    "browserCalling" | "browserCallingAvailable"
  >,
): boolean {
  return Boolean(
    status.browserCallingAvailable &&
    status.browserCalling.available &&
    status.browserCalling.state === "AVAILABLE",
  );
}

/**
 * Returns the backend's explicit call-start decision. During a rolling
 * deployment, an older response may omit the new field; only the legacy fully
 * available state is accepted as a safe fallback. Browser preparation is
 * never inferred from reason strings in the client.
 */
export function canStartBrowserCall(
  status: Pick<
    VoicePlatformStatus,
    "browserCalling" | "browserCallingAvailable"
  >,
): boolean {
  return status.browserCalling.canStartCall ?? isBrowserCallingAvailable(status);
}

export function browserCallingLabel(
  capability: BrowserCallingCapability,
): string {
  if (
    capability.canStartCall &&
    capability.startMode === "BROWSER_PREPARATION"
  ) {
    return "Ready on demand";
  }
  return labels[capability.state];
}

export function browserCallingReason(
  capability: BrowserCallingCapability,
): string | null {
  if (
    capability.canStartCall &&
    capability.startMode === "BROWSER_PREPARATION"
  ) {
    return "Your Browser phone is provisioned and will start when you place a call.";
  }
  if (capability.available) return null;
  for (const code of actionableReasonPriority) {
    if (capability.reasonCodes.includes(code)) return reasons[code];
  }
  for (const code of capability.reasonCodes) {
    const reason = reasons[code];
    if (reason) return reason;
  }
  if (capability.state === "PROVISIONING") {
    return "Employee voice setup is still being provisioned.";
  }
  if (capability.state === "UNKNOWN") {
    return "Current employee browser-calling status could not be confirmed.";
  }
  return "Your employee voice configuration is not ready for browser calling.";
}

export function isCurrentEmployeeVoiceEvent(
  payload: EmployeeVoiceChangedPayload,
  clientUserRef: string | null | undefined,
): boolean {
  if (!clientUserRef) return false;
  if (payload.clientUserRef) return payload.clientUserRef === clientUserRef;

  const prefix = "sparkone:user:";
  if (!clientUserRef.startsWith(prefix)) return false;
  return payload.tenantMembershipId === clientUserRef.slice(prefix.length);
}
